import Link from 'next/link';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { type ImageTool, mediaTemplates } from '@/constants/mediaTemplates';

// Outputs people build in the session, taken from the shared media templates
// so titles, links and previews stay in sync with the template pages.
const SHOWCASE = [
  { slug: 'generate-website', label: 'Marky Agent' },
  { slug: 'ai-slide-decks', label: 'Marky Agent' },
  { slug: 'product-showcase-reference', label: 'AI video' },
  { slug: 'social-media-video-ad', label: 'AI video' },
  { slug: 'ugc-product-review', label: 'AI video' },
  { slug: 'workflow-product-commercial', label: 'Workflow' },
  { slug: 'realtor-property-tour', label: 'AI video' },
  { slug: 'professional-headshot', label: 'AI image' },
];

const ITEMS = SHOWCASE.map(({ slug, label }) => {
  const template = mediaTemplates.find((item) => item.slug === slug);
  return template ? { ...template, label } : null;
}).filter((item): item is ImageTool & { label: string } => Boolean(item));

const isVideo = (item: ImageTool) =>
  item.isVideoPreview || /\.(mp4|webm)(\?|$)/i.test(item.image);

/** Mounts its children (a playing video) only once it nears the viewport. */
function WhenVisible({
  placeholder,
  children,
}: {
  placeholder: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={ref} className='h-full w-full'>
      {visible ? children : placeholder}
    </div>
  );
}

export default function WorkshopsShowcase() {
  const { ref, state } = useReveal<HTMLUListElement>(0.1);

  return (
    <section className='py-24 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='What you’ll make.'
          tail='In the session, not after it.'
          lead='A glimpse of what people build hands-on: websites, slide decks, product videos and marketing content, each from a ready-made template.'
        />
      </div>

      {/* Phones scroll the tiles sideways; larger screens show a grid. */}
      <ul
        ref={ref}
        data-reveal={state}
        className={clsxm(
          styles.reveal,
          'mt-14 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 [scrollbar-width:none] sm:mx-auto sm:grid sm:max-w-7xl sm:grid-cols-2 sm:overflow-visible sm:pb-0 lg:grid-cols-4 lg:px-8 [&::-webkit-scrollbar]:hidden',
        )}
      >
        {ITEMS.map((item, index) => (
          <li
            key={item.slug}
            className={clsxm(
              styles.rv,
              'w-[78%] flex-none snap-start sm:w-auto',
            )}
            style={delay((index % 4) * 70)}
          >
            <Link
              href={item.uri}
              className={clsxm(
                'group flex h-full flex-col overflow-hidden rounded-[22px] transition-shadow duration-300 hover:shadow-[0_24px_50px_-28px_rgba(30,27,75,0.5)]',
                tone.surface,
                tone.line,
              )}
            >
              <div className='relative aspect-[4/3] overflow-hidden bg-[#e8e8f0] dark:bg-white/5'>
                <span className='absolute start-3 top-3 z-10 rounded-full bg-[#0d0c1f]/75 px-2.5 py-0.5 text-xs font-semibold text-white backdrop-blur'>
                  {item.label}
                </span>
                {isVideo(item) ? (
                  <WhenVisible
                    placeholder={
                      <div className='h-full w-full animate-pulse bg-[#e8e8f0] dark:bg-white/5' />
                    }
                  >
                    <video
                      className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload='none'
                    >
                      <source
                        src={item.image}
                        type={
                          /\.webm(\?|$)/i.test(item.image)
                            ? 'video/webm'
                            : 'video/mp4'
                        }
                      />
                    </video>
                  </WhenVisible>
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={item.image}
                    alt=''
                    loading='lazy'
                    decoding='async'
                    className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
                  />
                )}
              </div>
              <div className='flex flex-1 flex-col p-5'>
                <h3
                  className={clsxm(
                    'text-[17px] font-semibold tracking-[-0.01em]',
                    tone.ink,
                  )}
                >
                  {item.title}
                </h3>
                <p
                  className={clsxm(
                    'mt-1.5 line-clamp-3 flex-1 text-sm leading-6',
                    tone.body,
                  )}
                >
                  {item.description}
                </p>
                <span className='mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 dark:text-primary-400'>
                  Open the template
                  <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5' />
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
